/**
 * Web component for Anear form submissions
 *
 * Usage:
 *   <anear-form action="SAVE_QUESTION_TEXT">
 *     <textarea name="text"></textarea>
 *     <button type="submit">Submit</button>
 *   </anear-form>
 *
 * The component will be initialized by useAppDisplayDomSetup with:
 * - action: The form action name (e.g., "SAVE_QUESTION_TEXT", "ADD_ANSWER")
 * - onFormSubmit: Callback function to handle form submissions
 */
class AnearForm extends HTMLElement {
  constructor() {
    super()
    this._action = null
    this._onFormSubmit = null
    this._boundSubmitHandler = null
    this._boundClickHandler = null
  }

  static get observedAttributes() {
    return ['action']
  }

  connectedCallback() {
    // Read the action attribute if it exists (attributeChangedCallback might not fire for initial attributes)
    const actionAttr = this.getAttribute('action')
    if (actionAttr !== null && this._action === null) {
      this._action = actionAttr
    }
    // Set up click interception for submit buttons since custom elements
    // don't automatically receive submit events
    this.setupSubmitButtonInterception()
    this.updateSubmitHandler()
  }

  /**
   * Intercept clicks on submit buttons within this form
   * Since custom elements don't receive native form submit events,
   * we need to manually handle submit button clicks
   * Also handles buttons outside the form that use form="formId" attribute
   */
  setupSubmitButtonInterception() {
    // Remove any existing click listener
    if (this._boundClickHandler) {
      this.removeEventListener('click', this._boundClickHandler, true)
    }

    // Use capture phase to catch clicks before they bubble
    this._boundClickHandler = (e) => {
      // If disabled due to timeout gating, block submit interactions entirely.
      if (this.getAttribute('aria-disabled') === 'true') {
        e.preventDefault?.()
        e.stopPropagation?.()
        // Best-effort: ask nearest timeout controller to show prompt.
        try {
          this.closest('anear-timeout')?.requestPrompt?.()
        } catch (_e) {
          // ignore
        }
        return false
      }

      // Find the submit button - check if target itself is a submit button,
      // or if it's inside one (using closest to walk up the tree)
      let submitButton = null
      if (e.target.type === 'submit' && (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT')) {
        submitButton = e.target
      } else {
        submitButton = e.target.closest('button[type="submit"], input[type="submit"]')
      }

      if (!submitButton) return

      // Check if button is inside this form OR references this form via form="formId"
      const formId = this.id
      const buttonFormAttr = submitButton.getAttribute('form')
      const isButtonInsideForm = this.contains(submitButton)
      const isButtonReferencingForm = formId && buttonFormAttr === formId

      // Only handle if the button is inside this form OR references it via form attribute
      if (isButtonInsideForm || isButtonReferencingForm) {
        // Prevent the default button behavior
        e.preventDefault()
        e.stopPropagation()

        // Create a synthetic submit event
        const submitEvent = new Event('submit', {
          bubbles: true,
          cancelable: true
        })
        // Set the submitter property (available in SubmitEvent)
        // This is important for getActionFormMessage to know which button was clicked
        Object.defineProperty(submitEvent, 'submitter', {
          value: submitButton,
          writable: false,
          configurable: false
        })
        // Set target to the form element itself so the handler can find it
        // (currentTarget will also be the form when dispatched on this element)
        Object.defineProperty(submitEvent, 'target', {
          value: this,
          writable: false,
          configurable: false
        })
        // Dispatch the event on this element
        this.dispatchEvent(submitEvent)
      }
    }

    this.addEventListener('click', this._boundClickHandler, true)
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'action') {
      this._action = newValue
      this.updateSubmitHandler()
    }
  }

  /**
   * Initialize the component with form submit handler
   * Called by useAppDisplayDomSetup
   */
  initialize(onFormSubmit) {
    this._onFormSubmit = onFormSubmit
    this.updateSubmitHandler()
  }

  get action() {
    return this.getAttribute('action') || this._action
  }

  set action(value) {
    if (value) {
      this.setAttribute('action', value)
      this._action = value
    } else {
      this.removeAttribute('action')
      this._action = null
    }
    this.updateSubmitHandler()
  }

  /**
   * Sets up the form submit handler
   * The component manages its own onsubmit binding
   */
  updateSubmitHandler() {
    const action = this.action
    const shouldHandleSubmit = action && action !== 'null' && this._onFormSubmit

    if (shouldHandleSubmit) {

      // Remove any existing listener first
      if (this._boundSubmitHandler) {
        this.removeEventListener('submit', this._boundSubmitHandler, false)
      }

      this._boundSubmitHandler = (e) => {
        // The handler will find this form via e.target.closest('anear-form')
        // and get the action from the 'action' attribute
        if (this._onFormSubmit) {
          this._onFormSubmit(e)
        }

        // Mobile Safari UX:
        // - Prevent iOS "input zoom" from sticking around after submit by blurring the focused field.
        // - Must not depend on app-specific action names; third-party apps can name actions freely.
        try {
          const ua = navigator.userAgent || ''
          const isIOS =
            /iPad|iPhone|iPod/.test(ua) ||
            // iPadOS reports as Mac; detect touch-capable "MacIntel" devices.
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
          const isWebKit = /WebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)

          if (isIOS && isWebKit) {
            const root = this.getRootNode?.()
            const activeEl = (root && root.activeElement) ? root.activeElement : document.activeElement

            const isTextField =
              activeEl &&
              (activeEl.tagName === 'INPUT' ||
                activeEl.tagName === 'TEXTAREA' ||
                activeEl.isContentEditable === true)

            if (isTextField && this.contains(activeEl) && typeof activeEl.blur === 'function') {
              activeEl.blur()
            }
          }
        } catch (_e) {
          // ignore
        }
      }

      // Use capture phase to ensure we catch the submit event
      // This works with both <button type="submit"> and <input type="submit">
      this.addEventListener('submit', this._boundSubmitHandler, false)
    } else {
      // If we don't have a handler yet, element will be ready when initialize() is called
      // Remove submit listener if it exists
      if (this._boundSubmitHandler) {
        this.removeEventListener('submit', this._boundSubmitHandler, false)
        this._boundSubmitHandler = null
      }
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this._boundSubmitHandler) {
      this.removeEventListener('submit', this._boundSubmitHandler, false)
      this._boundSubmitHandler = null
    }
    if (this._boundClickHandler) {
      this.removeEventListener('click', this._boundClickHandler, true)
      this._boundClickHandler = null
    }
  }
}

// Register the custom element
if (!customElements.get('anear-form')) {
  customElements.define('anear-form', AnearForm)
}

export default AnearForm
